import type { PostCompanyReviewSuccess } from '@seek/ca-graphql-schema/types';
import { useFeatureFlag as useCandidateFeatureFlag } from '@seek/candidate-feature-flag';
import { useConfig } from '@seek/libs-shared';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  LAUNCH_DARKLY_FEATURE_FLAGS_MAP,
  type LaunchDarklyFeatureFlagMap,
} from '../../shared/constants/launchDarklyFeatureFlags';
import { useFeatureFlags } from '../../shared/providers/featureFlagProvider';
import { useService } from '../../shared/providers/serviceProvider';
import type {
  ConfirmedRoles,
  RoleTitle,
} from '../../shared/services/roleService/roleService';
import {
  trackWriteAReviewSubmitted,
  type ReviewFormContext,
} from '../../shared/tracking/writeAReview/trackingEvents';
import type { ReviewPayload } from '../type';
import { mapActionOrigin } from '../utils/mapActionOrigin';
import { mapReview } from './mapper/reviewMapper';
import { WriteAReviewPage } from './WriteAReviewPage';

export const WriteAReviewContainer = () => {
  const urlSearchParams = new URL(window.location.href).searchParams;
  const companyNameQueryParam = urlSearchParams.get('cn') || undefined;
  const tracking = urlSearchParams.get('tracking') || undefined;

  const [roleTitleSessionId, setRoleTitleSessionId] = useState<string>(uuid());
  const [locationSessionId] = useState<string>(uuid());
  // use ReviewFormContext as state is becasue we may need to move the form to modal or drawer based form, having them integrated into the AppContext may cause redo in the future
  const reviewFormActionOrigin = mapActionOrigin(tracking);

  const [reviewFormContext, _setReviewFormContext] =
    useState<ReviewFormContext>({
      reviewId: uuid(),
      ...(reviewFormActionOrigin ? { reviewFormActionOrigin } : {}),
    });

  const config = useConfig();

  const {
    roleService,
    companyProfileService,
    locationService,
    companyReviewService,
  } = useService();

  const getRoleTitles = async (query: string): Promise<RoleTitle[]> => {
    // If text is cleared, generate a new session id
    let currentSessionId = roleTitleSessionId;
    if (query === '') {
      currentSessionId = uuid();
      setRoleTitleSessionId(currentSessionId);
    }

    return await roleService.getRoleTitles(query, currentSessionId);
  };

  const searchCompanyProfiles = async (query: string) => {
    return await companyProfileService.getCompanyProfilesByName(query, true);
  };

  const searchLocations = async (query: string) => {
    return await locationService.getLocations(query, locationSessionId);
  };

  const getConfirmedRoles = async () => {
    return await roleService.getConfirmedRoles();
  };

  const updateRole = async (role: ConfirmedRoles) => {
    return await roleService.updateRole(role);
  };

  const { cookieFeatureFlags } = useFeatureFlags();
  const reviewFormFlagKey =
    LAUNCH_DARKLY_FEATURE_FLAGS_MAP['company-review-form-prod'].key;
  const reviewFormFlagValue =
    useCandidateFeatureFlag<
      LaunchDarklyFeatureFlagMap[typeof reviewFormFlagKey]['values'][number]
    >(reviewFormFlagKey);
  // eslint-disable-next-line no-console
  console.log(reviewFormFlagValue); // TODO: remove this console when AA-testing for the 1st experiment is done.
  const isSimplifiedReviewForm =
    cookieFeatureFlags.SINGLE_PAGE_REVIEW_FLOW === true; // || reviewFormFlagValue === '2.0.0'
  const variantValueForTracking = reviewFormFlagValue
    ? `schema_v${reviewFormFlagValue}`
    : null;

  const handleSubmitReview = async (review: ReviewPayload) => {
    const mappedReview = mapReview(config.language, review);

    const response = await companyReviewService.submitReview(mappedReview);

    if (
      (response as PostCompanyReviewSuccess).reviewId &&
      variantValueForTracking
    ) {
      trackWriteAReviewSubmitted({
        config,
        context: reviewFormContext,
        variant: variantValueForTracking,
      });
      return true;
    }

    return false;
  };

  return (
    <WriteAReviewPage
      getRoleTitles={getRoleTitles}
      searchCompanyProfiles={searchCompanyProfiles}
      searchLocations={searchLocations}
      companyNameQueryParam={companyNameQueryParam}
      reviewFormContext={reviewFormContext}
      getConfirmedRoles={getConfirmedRoles}
      updateRole={updateRole}
      submitReview={handleSubmitReview}
      isSimplifiedReviewForm={isSimplifiedReviewForm}
      variantValueForTracking={variantValueForTracking}
    />
  );
};
